/**
 * ui/Combobox.tsx — ช่องกรอกพร้อม dropdown แนะนำ (autocomplete)
 *
 * ใช้กับการค้นลูกค้า/สินค้าในฟอร์มเอกสาร — พิมพ์แล้วเลือกจากรายการที่มีอยู่
 * หรือพิมพ์ชื่อใหม่ที่ไม่มีในระบบได้เลย (server จะบันทึกสินค้าใหม่ให้อัตโนมัติ)
 */

import { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from '~/lib/cn';
import { Input } from './primitives';

interface ComboboxProps<T> {
  value: string;
  onChange: (value: string) => void;
  onSelect: (item: T) => void;
  options: T[];
  isLoading?: boolean;
  getLabel: (item: T) => string;
  getSublabel?: (item: T) => string;
  placeholder?: string;
  invalid?: boolean;
}

export function Combobox<T>({
  value,
  onChange,
  onSelect,
  options,
  isLoading,
  getLabel,
  getSublabel,
  placeholder,
  invalid,
}: ComboboxProps<T>) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // ปิด dropdown เมื่อคลิกข้างนอก
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const showDropdown = open && value.trim().length > 0;

  return (
    <div ref={containerRef} className="relative">
      <Input
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        invalid={invalid}
        autoComplete="off"
      />

      {showDropdown ? (
        <div className="absolute z-30 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-border-base bg-surface py-1 shadow-md">
          {isLoading ? (
            <div className="flex items-center gap-2 px-3 py-2 text-xs text-fg-muted">
              <Loader2 size={13} className="animate-spin" />
              กำลังค้นหา…
            </div>
          ) : options.length === 0 ? (
            <p className="px-3 py-2 text-xs text-fg-muted">ไม่พบรายการ — พิมพ์เพื่อสร้างใหม่ได้</p>
          ) : (
            options.map((option, index) => (
              <button
                key={index}
                type="button"
                onClick={() => {
                  onSelect(option);
                  setOpen(false);
                }}
                className={cn(
                  'block w-full px-3 py-1.5 text-left text-xs hover:bg-surface-hover',
                )}
              >
                <span className="font-medium text-fg">{getLabel(option)}</span>
                {getSublabel ? (
                  <span className="ml-1.5 text-fg-muted">{getSublabel(option)}</span>
                ) : null}
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
